/**
 * fayn. Week Sperrseite (Passwortseite): Zeitphasen, Countdown und Dosen-Spiel.
 *
 * Phasen (nach Uhrzeit des Besuchers, Zeiten kommen aus dem Abschnitt):
 *   pre   vor dem Early Access: Spiel, Countdown, Anleitung
 *   early Early Access: Spiel beendet, Code-Feld sichtbar
 *   after nach dem Start fuer alle: Hinweis "Shop ist offen"
 * Zum Testen: ?fayn_lock_phase=pre|early|after in der Adresse.
 *
 * Elemente mit data-only="pre early after" werden nur in diesen Phasen gezeigt.
 */
const OVERLAY_READY_TITLE = 'Bereit?';
const OVERLAY_READY_TEXT = 'Zieh den Shaker mit dem Finger oder der Maus. Am Computer gehen auch die Pfeiltasten.';
const OVERLAY_START = 'Spiel starten';
const OVERLAY_LOST_TITLE = 'Knapp daneben';
const OVERLAY_RETRY = 'Nochmal versuchen';
const STORAGE_KEY = 'fayn-lock-won';

/**
 * Lage des Shakers im Bild (Fangbehaelter): Mitte der Oeffnung als Anteil der Bildbreite,
 * Hoehe der Oeffnung als Anteil der Bildhoehe und Breite der Fangflaeche als Anteil der Bildbreite.
 */
const CART_ANCHOR = 0.5;
const CART_RIM = 0.03;
const CART_CATCH = 0.9;

class FaynLock extends HTMLElement {
  /** @type {HTMLCanvasElement | null} */
  #cv = null;
  #ctx = null;
  #stage = null;
  #W = 360;
  #H = 450;
  #dpr = 1;
  #canImg = new Image();
  #cartImg = new Image();
  #cartRatio = 1.83;
  #state = { run: false, left: 30, caught: 0, cans: [], bx: 180, spawn: 0, last: 0, keys: {}, moved: false };
  #idleOn = false;
  #idleLast = 0;
  #idleSpawn = 0;
  #reduce = false;
  #gcVisible = false;
  #phase = 'pre';
  #won = false;
  #timer = 0;

  connectedCallback() {
    this.code = this.dataset.code || '';
    this.goal = Number(this.dataset.goal) || 20;
    this.seconds = Number(this.dataset.seconds) || 30;
    this.early = Number(this.dataset.early) || 0;
    this.open = Number(this.dataset.open) || 0;
    this.demo = this.dataset.demo === 'true';
    // Das Element mit der Fehlermeldung gibt es nur, wenn Shopify einen falschen Code gemeldet hat.
    this.hasError = !!this.querySelector('[data-el="error"]');
    this.#reduce = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    this.#state.left = this.seconds;

    try {
      this.#won = window.localStorage.getItem(STORAGE_KEY) === '1';
    } catch (e) {
      this.#won = false;
    }

    this.#cv = this.querySelector('[data-el="canvas"]');
    this.#stage = this.querySelector('[data-el="stage"]');
    if (this.#cv && this.#stage) {
      this.#ctx = this.#cv.getContext('2d');
      this.#canImg.src = this.dataset.can || '';
      this.#cartImg.src = this.dataset.cart || '';
      this.#canImg.onload = () => this.#draw();
      this.#cartImg.onload = () => {
        if (this.#cartImg.naturalWidth) this.#cartRatio = this.#cartImg.naturalHeight / this.#cartImg.naturalWidth;
        this.#draw();
      };
      this.#bindGame();
    }

    this.#bindUi();
    this.#applyPhase();
    this.#tickCountdown();
    this.#timer = window.setInterval(() => {
      const next = this.#computePhase();
      if (next !== this.#phase) this.#applyPhase();
      this.#tickCountdown();
      this.#updateSticky();
    }, 1000);
  }

  disconnectedCallback() {
    window.clearInterval(this.#timer);
    this.#state.run = false;
    this.#idleOn = false;
  }

  /* ---------- Phasen ---------- */

  #computePhase() {
    const override = new URLSearchParams(window.location.search).get('fayn_lock_phase');
    if (override === 'before' || override === 'pre') return 'pre';
    if (override === 'early' || override === 'after') return override;

    let phase = 'pre';
    const now = Date.now() / 1000;
    if (this.early && this.open) {
      if (now >= this.open) phase = 'after';
      else if (now >= this.early) phase = 'early';
    }
    // Wurde ein falscher Code abgeschickt, soll das Feld mit der Fehlermeldung sichtbar bleiben.
    if (this.hasError && phase === 'pre') phase = 'early';
    return phase;
  }

  #applyPhase() {
    this.#phase = this.#computePhase();
    this.dataset.phase = this.#phase;

    this.querySelectorAll('[data-only]').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      el.hidden = !(el.dataset.only || '').split(/\s+/).includes(this.#phase);
    });

    const pre = this.#phase === 'pre';
    const win = this.querySelector('[data-el="win"]');
    const gameCard = this.querySelector('[data-el="gamecard"]');
    const heroCta = this.querySelector('[data-el="herocta"]');
    if (win instanceof HTMLElement) win.hidden = !(pre && this.#won);
    if (gameCard instanceof HTMLElement && pre && this.#won) gameCard.hidden = true;
    if (heroCta instanceof HTMLElement && pre && this.#won) heroCta.hidden = true;

    const wonNote = this.querySelector('[data-el="wonnote"]');
    if (wonNote instanceof HTMLElement) wonNote.hidden = !(this.#phase === 'early' && this.#won);
    const input = this.querySelector('[data-el="input"]');
    if (input instanceof HTMLInputElement && this.#phase === 'early' && this.#won && !input.value) input.value = this.code;

    if (pre && !this.#won) this.#startIdle();
    this.#updateSticky();
  }

  #tickCountdown() {
    const tiles = this.querySelector('[data-el="tiles"]');
    if (!(tiles instanceof HTMLElement) || tiles.closest('[hidden]')) return;
    const target = this.#phase === 'early' ? this.open : this.early;
    const s = Math.max(0, Math.floor(target - Date.now() / 1000));
    const parts = [
      [Math.floor(s / 86400), 'Tage'],
      [Math.floor((s % 86400) / 3600), 'Std'],
      [Math.floor((s % 3600) / 60), 'Min'],
      [s % 60, 'Sek']
    ];
    tiles.replaceChildren(
      ...parts.map(([value, label]) => {
        const tile = document.createElement('i');
        tile.textContent = String(value).padStart(2, '0');
        const span = document.createElement('span');
        span.textContent = label;
        tile.appendChild(span);
        return tile;
      })
    );
  }

  /* ---------- Bedienelemente ---------- */

  #bindUi() {
    const play = () => this.#goPlay();
    this.querySelectorAll('[data-action="play"]').forEach((btn) => btn.addEventListener('click', play));

    this.querySelector('[data-action="again"]')?.addEventListener('click', () => {
      this.#won = false;
      try {
        window.localStorage.removeItem(STORAGE_KEY);
      } catch (e) {
        /* ohne Speicher */
      }
      this.#resetOverlay();
      this.#reset();
      this.#size();
      this.#applyPhase();
    });

    this.querySelector('[data-action="copy"]')?.addEventListener('click', () => {
      const note = this.querySelector('[data-el="copymsg"]');
      const done = () => {
        if (note) note.textContent = this.dataset.copied || 'Code kopiert.';
      };
      const select = () => {
        const node = this.querySelector('[data-el="codetxt"]');
        if (node) {
          const range = document.createRange();
          range.selectNodeContents(node);
          const sel = window.getSelection();
          sel?.removeAllRanges();
          sel?.addRange(range);
        }
        if (note) note.textContent = this.dataset.selected || 'Code markiert, bitte kopieren.';
      };
      try {
        navigator.clipboard.writeText(this.code).then(done, select);
      } catch (e) {
        select();
      }
    });

    this.querySelector('[data-action="reload"]')?.addEventListener('click', () => window.location.reload());

    const input = this.querySelector('[data-el="input"]');
    if (input instanceof HTMLInputElement && this.code && this.code === this.code.toUpperCase()) {
      input.addEventListener('input', () => {
        const pos = input.selectionStart;
        input.value = input.value.toUpperCase();
        if (pos !== null) input.setSelectionRange(pos, pos);
      });
    }

    const form = this.querySelector('[data-el="form"]');
    if (form instanceof HTMLFormElement && this.demo) {
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        const msg = this.querySelector('[data-el="demomsg"]');
        if (msg) msg.textContent = 'Testseite: Auf der echten Passwortseite wird der Code jetzt von Shopify geprüft.';
      });
    }

    const sticky = this.querySelector('[data-el="sticky"]');
    if (sticky && window.IntersectionObserver) {
      const target = this.querySelector('[data-el="gamecard"]');
      if (target) {
        new IntersectionObserver(
          (entries) => {
            this.#gcVisible = entries[0].isIntersecting;
            this.#updateSticky();
          },
          { threshold: 0.25 }
        ).observe(target);
      }
    }
  }

  #updateSticky() {
    const sticky = this.querySelector('[data-el="sticky"]');
    if (!(sticky instanceof HTMLElement)) return;
    const gameCard = this.querySelector('[data-el="gamecard"]');
    const hide = this.#phase !== 'pre' || this.#won || this.#gcVisible || this.#state.run || (gameCard instanceof HTMLElement && gameCard.hidden);
    sticky.classList.toggle('is-off', hide);
    sticky.setAttribute('aria-hidden', hide ? 'true' : 'false');
    this.querySelector('[data-el="cta"]')?.classList.toggle('is-stopped', this.#state.run || this.#won);
  }

  #goPlay() {
    const card = this.querySelector('[data-el="gamecard"]');
    if (!(card instanceof HTMLElement) || card.hidden) return;
    card.scrollIntoView({ behavior: this.#reduce ? 'auto' : 'smooth', block: 'center' });
    window.setTimeout(() => {
      if (!this.#state.run) this.#start();
    }, 500);
  }

  /* ---------- Spiel ---------- */

  #bindGame() {
    const cv = this.#cv;
    if (!cv) return;
    cv.addEventListener('pointerdown', (e) => this.#moveTo(e.clientX));
    cv.addEventListener('pointermove', (e) => this.#moveTo(e.clientX));
    window.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        this.#state.keys[e.key] = true;
        if (this.#state.run) e.preventDefault();
      }
    });
    window.addEventListener('keyup', (e) => {
      this.#state.keys[e.key] = false;
    });
    this.querySelector('[data-el="go"]')?.addEventListener('click', () => this.#start());
    window.addEventListener('resize', () => this.#size());
    if (window.ResizeObserver && this.#stage) new ResizeObserver(() => this.#size()).observe(this.#stage);
    this.#reset();
    this.#size();
  }

  #dims() {
    const W = this.#W;
    const H = this.#H;
    let sw = Math.max(90, Math.min(150, W * 0.32));
    // Hoher Behaelter: nicht mehr als 40 Prozent der Spielfeldhoehe.
    if (sw * this.#cartRatio > H * 0.4) sw = (H * 0.4) / this.#cartRatio;
    const sh = sw * this.#cartRatio;
    return {
      sw,
      sh,
      bw: sw * CART_CATCH,
      bh: Math.max(30, sh * 0.2),
      by: H - 12 - sh + sh * CART_RIM,
      minX: sw * CART_ANCHOR,
      maxX: W - sw * (1 - CART_ANCHOR),
      cw: Math.max(34, Math.min(46, W * 0.11))
    };
  }

  #size() {
    if (!this.#cv || !this.#stage || !this.#ctx) return;
    this.#W = this.#stage.clientWidth || 360;
    this.#H = Math.round(this.#W * 1.2);
    this.#dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.#cv.width = Math.round(this.#W * this.#dpr);
    this.#cv.height = Math.round(this.#H * this.#dpr);
    this.#cv.style.height = `${this.#H}px`;
    this.#ctx.setTransform(this.#dpr, 0, 0, this.#dpr, 0, 0);
    if (!this.#state.run) {
      if (!this.#state.moved) this.#state.bx = this.#W / 2 + this.#dims().sw * (CART_ANCHOR - 0.5);
      this.#draw();
    }
  }

  #reset() {
    const s = this.#state;
    s.run = false;
    s.left = this.seconds;
    s.caught = 0;
    s.cans = [];
    s.spawn = 0;
    s.bx = this.#W / 2;
    this.#updateHud();
  }

  #updateHud() {
    const sc = this.querySelector('[data-el="score"]');
    const tm = this.querySelector('[data-el="time"]');
    if (sc) sc.textContent = String(this.#state.caught);
    if (tm) tm.textContent = String(Math.max(0, Math.ceil(this.#state.left)));
  }

  #spawnCan() {
    const d = this.#dims();
    const s = this.#state;
    const progress = 1 - s.left / this.seconds;
    s.cans.push({
      x: d.cw / 2 + Math.random() * (this.#W - d.cw),
      y: -d.cw * 1.6,
      v: 120 + Math.random() * 70 + progress * 110,
      r: (Math.random() - 0.5) * 0.6,
      rv: (Math.random() - 0.5) * 2.2
    });
  }

  #draw() {
    const ctx = this.#ctx;
    if (!ctx) return;
    const W = this.#W;
    const H = this.#H;
    const d = this.#dims();
    const s = this.#state;
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = '#eef2f7';
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = '#dfe6ef';
    ctx.fillRect(0, H - 24, W, 24);

    const can = this.#canImg;
    const canReady = can.complete && can.naturalWidth > 0;
    const ch = d.cw * (canReady ? can.naturalHeight / can.naturalWidth : 1.57);
    s.cans.forEach((c) => {
      ctx.save();
      ctx.translate(c.x, c.y);
      ctx.rotate(c.r);
      if (canReady) {
        ctx.drawImage(can, -d.cw / 2, -ch / 2, d.cw, ch);
      } else {
        ctx.fillStyle = '#111';
        ctx.fillRect(-d.cw / 2, -ch / 2, d.cw, ch);
      }
      ctx.restore();
    });

    const cart = this.#cartImg;
    if (cart.complete && cart.naturalWidth > 0) {
      ctx.drawImage(cart, s.bx - d.sw * CART_ANCHOR, H - 12 - d.sh, d.sw, d.sh);
    } else {
      ctx.fillStyle = '#1c1c1c';
      ctx.fillRect(s.bx - d.bw / 2, d.by, d.bw, d.bh);
    }
  }

  #moveTo(clientX) {
    if (!this.#cv) return;
    const s = this.#state;
    s.moved = true;
    const rect = this.#cv.getBoundingClientRect();
    const d = this.#dims();
    s.bx = Math.max(d.minX, Math.min(d.maxX, clientX - rect.left));
    if (!s.run) this.#draw();
  }

  #start() {
    this.#reset();
    const ov = this.querySelector('[data-el="overlay"]');
    if (ov instanceof HTMLElement) ov.hidden = true;
    const s = this.#state;
    s.run = true;
    s.last = performance.now();
    this.#updateSticky();
    requestAnimationFrame((ts) => this.#tick(ts));
  }

  #tick(ts) {
    const s = this.#state;
    if (!s.run) return;
    const dt = Math.min(0.05, (ts - s.last) / 1000 || 0);
    s.last = ts;
    const d = this.#dims();
    const speed = 360;
    if (s.keys.ArrowLeft) s.bx -= speed * dt;
    if (s.keys.ArrowRight) s.bx += speed * dt;
    s.bx = Math.max(d.minX, Math.min(d.maxX, s.bx));
    s.left -= dt;
    s.spawn -= dt;
    if (s.spawn <= 0) {
      this.#spawnCan();
      s.spawn = 0.45 + Math.random() * 0.3;
    }
    const ch = d.cw * 1.57;
    for (let i = s.cans.length - 1; i >= 0; i--) {
      const c = s.cans[i];
      c.y += c.v * dt;
      c.r += c.rv * dt;
      if (c.y + ch * 0.4 >= d.by && c.y < d.by + d.bh && Math.abs(c.x - s.bx) < d.bw / 2) {
        s.cans.splice(i, 1);
        s.caught++;
        if (s.caught >= this.goal) {
          this.#finish(true);
          return;
        }
      } else if (c.y - ch > this.#H) {
        s.cans.splice(i, 1);
      }
    }
    this.#updateHud();
    this.#draw();
    if (s.left <= 0) {
      this.#finish(false);
      return;
    }
    requestAnimationFrame((next) => this.#tick(next));
  }

  #finish(win) {
    const s = this.#state;
    s.run = false;
    this.#updateHud();
    this.#draw();
    if (win) {
      this.#won = true;
      try {
        window.localStorage.setItem(STORAGE_KEY, '1');
      } catch (e) {
        /* ohne Speicher */
      }
      this.#applyPhase();
      this.querySelector('[data-el="win"]')?.scrollIntoView({ behavior: this.#reduce ? 'auto' : 'smooth', block: 'center' });
    } else {
      const title = this.querySelector('[data-el="overlay-title"]');
      const text = this.querySelector('[data-el="overlay-text"]');
      const go = this.querySelector('[data-el="go"]');
      const ov = this.querySelector('[data-el="overlay"]');
      if (title) title.textContent = OVERLAY_LOST_TITLE;
      if (text) text.textContent = `Du hast ${s.caught} von ${this.goal} Dosen gefangen. Noch ein Versuch?`;
      if (go) go.textContent = OVERLAY_RETRY;
      if (ov instanceof HTMLElement) ov.hidden = false;
      this.#updateSticky();
      this.#startIdle();
    }
  }

  #resetOverlay() {
    const title = this.querySelector('[data-el="overlay-title"]');
    const text = this.querySelector('[data-el="overlay-text"]');
    const go = this.querySelector('[data-el="go"]');
    const ov = this.querySelector('[data-el="overlay"]');
    if (title) title.textContent = OVERLAY_READY_TITLE;
    if (text) text.textContent = OVERLAY_READY_TEXT;
    if (go) go.textContent = OVERLAY_START;
    if (ov instanceof HTMLElement) ov.hidden = false;
  }

  /** Vor dem Start fallen langsam ein paar Dosen, das zieht den Blick auf das Spiel. */
  #startIdle() {
    if (this.#idleOn || this.#reduce || !this.#ctx) return;
    this.#idleOn = true;
    this.#idleLast = performance.now();
    requestAnimationFrame((ts) => this.#idle(ts));
  }

  #idle(ts) {
    const s = this.#state;
    const card = this.querySelector('[data-el="gamecard"]');
    if (s.run || this.#reduce || (card instanceof HTMLElement && card.hidden)) {
      this.#idleOn = false;
      return;
    }
    const dt = Math.min(0.05, (ts - this.#idleLast) / 1000 || 0);
    this.#idleLast = ts;
    this.#idleSpawn -= dt;
    if (this.#idleSpawn <= 0) {
      this.#spawnCan();
      this.#idleSpawn = 0.9;
      s.cans[s.cans.length - 1].v = 60 + Math.random() * 30;
    }
    for (let i = s.cans.length - 1; i >= 0; i--) {
      const c = s.cans[i];
      c.y += c.v * dt;
      c.r += c.rv * dt * 0.5;
      if (c.y > this.#H + 60) s.cans.splice(i, 1);
    }
    this.#draw();
    requestAnimationFrame((next) => this.#idle(next));
  }
}

if (!customElements.get('fayn-lock')) customElements.define('fayn-lock', FaynLock);
